import api from "@/lib/api";

export const saveCoupon = (body) => api.post("/cc/coupons", body).then((r) => r.data);
export const setCouponActive = (code, active, archived = false) => api.post(`/cc/coupons/${encodeURIComponent(code)}/active`, { active, archived }).then((r) => r.data);
export const deleteCoupon = (code) => api.delete(`/cc/coupons/${encodeURIComponent(code)}`).then((r) => r.data);
export const simulateCoupon = (body) => api.post("/cc/coupons/simulate", body).then((r) => r.data);
// Bulk single-use codes
export const makeBatch = (body) => api.post("/cc/coupons/batch", body).then((r) => r.data);
export const setBatchActive = (batchId, active) => api.post(`/cc/coupons/batch/${encodeURIComponent(batchId)}/active`, { active }).then((r) => r.data);
// Product collections
export const saveCollection = (body) => api.post("/cc/coupons/collections", body).then((r) => r.data);
export const deleteCollection = (id) => api.delete(`/cc/coupons/collections/${encodeURIComponent(id)}`).then((r) => r.data);
// Automatic offers
export const saveAutomation = (body) => api.post("/cc/coupons/automation", body).then((r) => r.data);
export const runAutomation = (dryRun = true) => api.post("/cc/coupons/automation/run", { dry_run: dryRun }).then((r) => r.data);
export const giveOffer = (body) => api.post("/cc/coupons/grants", body).then((r) => r.data);
export const updateGrant = (id, action, extra = {}) => api.post(`/cc/coupons/grants/${encodeURIComponent(id)}`, { action, ...extra }).then((r) => r.data);

// Same vocabulary as backend/coupons.py
export const SCOPES = [
  { key: "lead_fee", label: "Start a project fee", hint: "the lead float fee (₹499)" },
  { key: "raw_materials", label: "Raw materials", hint: "cement, paint, pipes…" },
  { key: "finished_goods", label: "Finished goods", hint: "fans, lights, furniture…" },
  { key: "promise_fee", label: "Brugo Promise fee", hint: "the one-time Promise fee" },
  { key: "milestone_payment", label: "Promise milestone payments", hint: "advance payments per milestone" },
];
export const SCOPE_LABEL = Object.fromEntries(SCOPES.map((s) => [s.key, s.label]));
export const MATERIAL_SCOPES = ["raw_materials", "finished_goods"];

export const TRIGGERS = [
  { key: "new_customer", label: "New customers", icon: "Sparkles", hint: "Their first order — applied by itself, no code." },
  { key: "drop_off", label: "Customers who dropped off", icon: "LogOut", hint: "Stopped part-way — they get a personal offer for a few days." },
  { key: "win_back", label: "Win back inactive customers", icon: "RotateCcw", hint: "No order for a while — a personal offer to come back." },
  { key: "loyalty", label: "Loyal repeat customers", icon: "Crown", hint: "After N orders or ₹X spent — applied by itself." },
  { key: "next_order", label: "Thank-you for next order", icon: "Gift", hint: "After a paid order — a personal offer for the next one." },
  { key: "service_booked", label: "Booked a service", icon: "Lightbulb", hint: "Paid the start-project fee on a special brugo.in form (e.g. Diwali Lighting): the personal offer is given at once." },
];
// brugo.in Fix My Home forms that can give a "Booked a service" offer.
export const FORMS = [{ key: "diwali_lighting", label: "Diwali Lighting installation" }];
export const FORM_LABEL = Object.fromEntries(FORMS.map((x) => [x.key, x.label]));
export const TRIGGER_LABEL = Object.fromEntries(TRIGGERS.map((t) => [t.key, t.label]));
export const GRANT_TRIGGERS = ["drop_off", "win_back", "next_order", "service_booked"];
export const STAGES = [
  { key: "lead_checkout_left", label: "Left the Start-a-project payment" },
  { key: "materials_checkout_left", label: "Left the materials checkout" },
  { key: "payment_failed", label: "Payment failed and didn't retry" },
  { key: "promise_unpaid", label: "Didn't pay the Brugo Promise fee" },
  { key: "quotes_not_hired", label: "Got quotes but didn't hire" },
  { key: "signed_up_no_start", label: "Signed up but never started" },
  { key: "cart_left", label: "Left items in the cart", note: "works once brugo.in saves carts" },
];
export const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));

const inrs = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const num = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));

// Customer-facing one-liner (mirrors coupons.describe on the server).
export function describeCoupon(c = {}) {
  const t = c.discount_type || "percent";
  let head;
  if (t === "percent") head = `${Number(c.amount) || 0}% off${c.max_discount ? ` up to ${inrs(c.max_discount)}` : ""}`;
  else if (t === "flat") head = `${inrs(c.amount)} off`;
  else if (t === "tiered") {
    const tiers = (c.tiers || []).filter((x) => num(x.min) != null && num(x.value) != null).sort((a, b) => a.min - b.min);
    const unit = (x) => (c.tier_basis === "qty" ? `${Number(x.min)}+ units` : `${inrs(x.min)}+`);
    const val = (x) => (c.tier_value_type === "flat" ? `${inrs(x.value)} off` : `${Number(x.value)}% off`);
    head = tiers.length ? `Buy more, save more: ${tiers.map((x) => `${val(x)} on ${unit(x)}`).join(" · ")}` : "Bulk offer";
    if (c.tier_value_type !== "flat" && c.max_discount) head += ` (up to ${inrs(c.max_discount)})`;
  } else head = "Free delivery";
  if (t !== "free_delivery" && c.free_delivery) head += " + free delivery";
  const on = (c.applies_to || []).map((s) => (SCOPE_LABEL[s] || s).toLowerCase()).join(", ") || "nothing yet";
  return `${head} on ${on}${c.min_order ? `, min ${inrs(c.min_order)}` : ""}`;
}

const span = (h) => { const n = Number(h) || 0; return n >= 24 && n % 24 === 0 ? `${n / 24} day${n === 24 ? "" : "s"}` : `${n} hours`; };
// Staff-facing: how an automatic offer is given.
export function describeTrigger(c = {}) {
  const t = c.trigger;
  if (c.mode !== "auto" || !t?.type) return "Customer types the code";
  const wait = t.wait_hours ?? 24, offer = t.offer_hours ?? 72;
  if (t.type === "new_customer") return `Applied by itself on a customer's first order${t.signup_within_days ? ` (within ${t.signup_within_days} days of signing up)` : ""}`;
  if (t.type === "loyalty") return `Applied by itself for loyal customers: ${[t.min_orders ? `${t.min_orders}+ paid orders` : "", t.min_spend ? `${inrs(t.min_spend)}+ spent` : ""].filter(Boolean).join(" and ") || "—"}`;
  if (t.type === "drop_off") return `Given ${span(wait)} after a customer ${(t.stages || []).map((s) => (STAGE_LABEL[s] || s).toLowerCase()).join(" / ") || "—"} · lasts ${span(offer)}`;
  if (t.type === "win_back") return `Given when a customer has no order for ${t.inactive_days ?? 90} days · lasts ${span(offer)}`;
  if (t.type === "service_booked") return `Given when a customer pays for a ${(t.forms || []).map((f) => FORM_LABEL[f] || f).join(" / ") || "—"} request · lasts ${span(offer)}`;
  return `Given after each paid order, for the next one · lasts ${span(offer)}`;
}

// JS mirror of coupons.collection_has (used to preview what's in a collection).
export function collectionHas(coll = {}, p = {}) {
  const id = String(p.id || "");
  if ((coll.product_ids || []).includes(id)) return true;
  if ((coll.exclude_product_ids || []).includes(id)) return false;
  const r = coll.rules || {};
  const cats = (r.categories || []).map((x) => String(x).toLowerCase());
  const kinds = (r.kinds || []).map((x) => String(x).toLowerCase());
  const words = String(r.name_contains || "").trim().toLowerCase();
  const lo = num(r.min_price), hi = num(r.max_price);
  if (!cats.length && !kinds.length && !words && lo == null && hi == null) return false;
  const kind = String(p.kind || p.product_type || "").toLowerCase() === "finished" || p.is_finished ? "finished" : "raw";
  const price = Number(p.price ?? p.sale_price ?? 0);
  return (!cats.length || [p.category, p.shop_category].some((x) => x && cats.includes(String(x).toLowerCase())))
    && (!kinds.length || kinds.includes(kind))
    && (!words || String(p.name || "").toLowerCase().includes(words))
    && (lo == null || price >= lo) && (hi == null || price <= hi);
}

export const REASON_TEXT = {
  ok: "Applies", invalid: "Code doesn't exist", inactive: "Paused", not_started: "Not started yet", expired: "Expired",
  not_applicable: "Not for this payment", not_for_you: "Not for this customer", new_only: "First order only",
  not_new: "Welcome window over", not_loyal: "Not loyal enough yet", no_grant: "Customer wasn't given this offer",
  wrong_city: "Different city", used_up: "Fully used", already_used: "Already used by customer", below_min: "Below minimum",
  below_tier: "Below the first bulk step", no_wallet: "Can't combine with wallet", no_discount: "No discount",
};

// WhatsApp click-to-chat for a personal offer (opens WhatsApp with the message ready to send).
export function whatsappLink(grant = {}) {
  const phone = String(grant.phone || "").replace(/\D/g, "").slice(-10);
  const name = grant.name ? String(grant.name).split(" ")[0] : "there";
  const until = grant.expires_at ? new Date(grant.expires_at?.toDate ? grant.expires_at.toDate() : grant.expires_at) : null;
  const when = until && !Number.isNaN(until.getTime()) ? until.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "soon";
  const msg = `Hi ${name}, this is Brugo. We've kept a special offer for you: ${grant.offer_text || "a discount"}. It applies by itself at checkout on brugo.in (code ${grant.code}). Valid till ${when}. Reply here if you need any help!`;
  return phone ? `https://wa.me/91${phone}?text=${encodeURIComponent(msg)}` : null;
}
